import { useRef } from 'react';

import { useNavigate, useParams } from 'react-router';

import { useMutation, UseMutationResult } from '@tanstack/react-query';
import { FormikHelpers } from 'formik';
import { useSnackbar } from 'notistack';

import { useApiContext } from 'context/ApiContext';
import { useShowError } from 'hooks/useShowError';
import { getFieldsError } from 'util/error';
import { getSuccessMessage } from 'util/success';

interface MutationOptions {
    url: string;
    formData?: any;
    idField?: string;
    preventSnack?: boolean;
    method?: 'POST' | 'PUT';
    preventRedirect?: boolean;
    formik?: FormikHelpers<any>;
    cdIdempotency?: string | null;
}

export const useFormMutation = <TData = unknown, TError = unknown>(): UseMutationResult<TData, TError, MutationOptions> => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { showError } = useShowError();
    const { enqueueSnackbar } = useSnackbar();
    const { api } = useApiContext();

    const idProperty = useRef<string | undefined>(null);
    const noSnack = useRef<boolean | undefined>(null);
    const noRedirect = useRef<boolean | undefined>(null);
    const formik = useRef<FormikHelpers<any> | undefined>(null);

    const mutation = useMutation<TData, TError, MutationOptions>(
        ({
            url,
            method,
            idField,
            formData,
            cdIdempotency,
            preventSnack = false,
            preventRedirect = false,
            formik: formikHelpers,
        }: MutationOptions) => {
            const id = idField ? formData[idField] : null;

            idProperty.current = idField;
            noSnack.current = preventSnack;
            noRedirect.current = preventRedirect;
            formik.current = formikHelpers;

            if (method !== 'POST' && (typeof id === 'string' || (typeof id === 'number' && id > 0))) {
                return api.put(`${url.endsWith('/') ? url : `${url}/`}${id}`, formData);
            }

            if (method === 'PUT') {
                return api.put(url, formData);
            }

            if (cdIdempotency) {
                formData.cdIdempotency = cdIdempotency;
            }

            return api.post(url, formData);
        },
        {
            onSuccess: (result: any) => {
                const message = getSuccessMessage(result);
                const registeredId = idProperty.current && result.data.data[idProperty.current];

                if (formik.current) {
                    formik.current.setErrors({});
                }

                if (!noSnack.current) {
                    enqueueSnackbar(message, { variant: 'success' });
                }

                if (!noRedirect.current && registeredId && String(registeredId) !== String(id)) {
                    // aguardar formik atualizar states
                    setTimeout(() => {
                        navigate(registeredId.toString(), { replace: true });
                    }, 150);
                }
            },
            onError: (error) => {
                const fields = getFieldsError(error);

                if (formik.current) {
                    formik.current.setErrors({});

                    if (fields) {
                        formik.current.setErrors(fields);
                    }
                }

                if (!noSnack.current && !fields) {
                    showError(error);
                }
            },
        },
    );

    return {
        ...mutation,
        mutate: (...args: [MutationOptions]) => {
            if (!mutation.isLoading) {
                mutation.mutate(...args);
            }
        },
    };
};
